/**
 * src/services/teleportSessionService.ts
 *
 * Tiny browser WS client for the cinematic AI-agent teleport
 * watch-party hub at `/ws/teleport`. The server is responsible for
 * picking a host (first joiner) and auto-promoting on host
 * disconnect — this client just streams the role + state frames
 * the hook needs to switch between "I drive the camera" (host) and
 * "I mirror the host's camera" (viewer).
 *
 * The hook uses ONE `TeleportSessionHandle` per session (i.e. per
 * `agentPk`). Joining a different agent tears the existing one
 * down with `close()`.
 *
 * Auto-reconnect uses the same exponential schedule as the agent
 * feed client, and on each fresh socket we re-`join()` so a brief
 * network blip doesn't drop the user out of the watch party. The
 * server treats a duplicate join from the same connection as
 * idempotent (it just re-sends the welcome).
 */

export type TeleportRole = 'host' | 'viewer';

export interface TeleportLngLat { lat: number; lng: number; }

/** Mirror of the camera options shape MapboxGl.flyTo accepts. */
export interface TeleportCameraOptions {
  center?:   [number, number];
  zoom?:     number;
  pitch?:    number;
  bearing?:  number;
  duration?: number;
  curve?:    number;
  speed?:    number;
  essential?: boolean;
}

export interface TeleportCameraActionFrame {
  type:    'camera_action';
  agentPk: string;
  action:  'flyTo' | 'easeTo' | 'jumpTo' | 'stop';
  options?: TeleportCameraOptions;
  ts:      number;
}

export interface TeleportPhaseFrame {
  type:    'phase';
  agentPk: string;
  phase:   'idle'|'entering'|'flying'|'arrived'|'rotating'|'countdown'|'exiting';
  currentStopIndex: number;
  countdownSeconds: number | null;
  ts: number;
}

export interface TeleportQueueFrame {
  type:    'queue';
  agentPk: string;
  queue:   TeleportLngLat[];
  ts:      number;
}

export interface TeleportPresenceMember {
  id:        string;
  joinedAt:  number;
  walletPk:  string | null;
}

export interface TeleportWelcomeFrame {
  type:     'welcome';
  agentPk:  string;
  role:     TeleportRole;
  selfId:   string;
  hostId:   string | null;
  viewers:  TeleportPresenceMember[];
  retained: {
    camera_action?: TeleportCameraActionFrame;
    phase?:         TeleportPhaseFrame;
    queue?:         TeleportQueueFrame;
  };
}

export interface TeleportPresenceFrame {
  type:    'presence';
  agentPk: string;
  hostId:  string | null;
  viewers: TeleportPresenceMember[];
}

export interface TeleportRoleChangeFrame {
  type:    'role_change';
  agentPk: string;
  role:    TeleportRole;
  hostId:  string | null;
}

export type TeleportFrame =
  | { type: 'hello'; at: number }
  | { type: 'pong';  at: number }
  | { type: 'session_ended'; agentPk: string }
  | TeleportWelcomeFrame
  | TeleportPresenceFrame
  | TeleportRoleChangeFrame
  | TeleportCameraActionFrame
  | TeleportPhaseFrame
  | TeleportQueueFrame;

export interface TeleportSessionHandle {
  /**
   * Send a host-only frame. Silently dropped server-side when
   * we're not the host (no NACK so the demoted-host race doesn't
   * surface as an error). Caller should additionally guard at the
   * hook level by consulting the latest known role.
   */
  publishCameraAction: (action: TeleportCameraActionFrame['action'], options?: TeleportCameraOptions) => void;
  publishPhase:        (phase: TeleportPhaseFrame['phase'], currentStopIndex: number, countdownSeconds: number | null) => void;
  publishQueue:        (queue: TeleportLngLat[]) => void;

  /** Fire-and-forget leave. Same as close() — kept as an alias so the hook reads cleanly. */
  leave:               () => void;
  close:               () => void;
}

export interface OpenTeleportSessionOpts {
  agentPk:  string;
  walletPk?: string;
  onFrame:  (frame: TeleportFrame) => void;
  onError?: (err: Event | unknown) => void;
}

const RETRY_MS = [1_000, 2_000, 5_000, 10_000, 30_000];

export function openTeleportSession(opts: OpenTeleportSessionOpts): TeleportSessionHandle {
  let ws: WebSocket | null = null;
  let closed = false;
  let retry  = 0;

  const url = (() => {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}/ws/teleport`;
  })();

  const send = (m: object) => { try { ws?.send(JSON.stringify(m)); } catch { /* swallow */ } };

  const sendJoin = () => send({ type: 'join', agentPk: opts.agentPk, walletPk: opts.walletPk || undefined });

  const connect = () => {
    if (closed) return;
    try {
      ws = new WebSocket(url);
    } catch (err) {
      opts.onError?.(err);
      scheduleReconnect();
      return;
    }
    ws.onopen = () => {
      retry = 0;
      sendJoin();
    };
    ws.onmessage = (evt) => {
      let frame: TeleportFrame | null = null;
      try { frame = JSON.parse(String(evt.data)); } catch { return; }
      if (!frame) return;
      try { opts.onFrame(frame); } catch { /* user handler error */ }
    };
    ws.onerror = (err) => { opts.onError?.(err); };
    ws.onclose = () => {
      ws = null;
      if (!closed) scheduleReconnect();
    };
  };

  const scheduleReconnect = () => {
    if (closed) return;
    const delay = RETRY_MS[Math.min(retry, RETRY_MS.length - 1)];
    retry += 1;
    setTimeout(connect, delay);
  };

  connect();

  return {
    publishCameraAction(action, options) {
      send({ type: 'camera_action', agentPk: opts.agentPk, action, options, ts: Date.now() });
    },
    publishPhase(phase, currentStopIndex, countdownSeconds) {
      send({
        type: 'phase',
        agentPk: opts.agentPk,
        phase,
        currentStopIndex,
        countdownSeconds,
        ts: Date.now(),
      });
    },
    publishQueue(queue) {
      send({ type: 'queue', agentPk: opts.agentPk, queue, ts: Date.now() });
    },
    leave() {
      send({ type: 'leave', agentPk: opts.agentPk });
    },
    close() {
      closed = true;
      send({ type: 'leave', agentPk: opts.agentPk });
      try { ws?.close(); } catch { /* swallow */ }
      ws = null;
    },
  };
}
